import { useState, useCallback, useMemo } from "react";
import type { EmailReference, EmailSelectionState } from "./types";

/**
 * Hook for managing email selection state
 * @returns Selection state and actions
 */
export function useEmailSelection(): EmailSelectionState {
  const [selectedEmails, setSelectedEmails] = useState<Map<string, EmailReference>>(
    new Map()
  );

  const selectedCount = useMemo(() => selectedEmails.size, [selectedEmails]);

  const isSelected = useCallback(
    (messageId: string) => selectedEmails.has(messageId),
    [selectedEmails]
  );

  const toggleSelect = useCallback(
    (messageId: string, ref: EmailReference) => {
      setSelectedEmails((prev) => {
        const next = new Map(prev);
        if (next.has(messageId)) {
          next.delete(messageId);
        } else {
          next.set(messageId, ref);
        }
        return next;
      });
    },
    []
  );

  const selectAll = useCallback(
    (emails: Array<{ messageId: string; ref: EmailReference }>) => {
      setSelectedEmails((prev) => {
        const next = new Map(prev);
        for (const { messageId, ref } of emails) {
          next.set(messageId, ref);
        }
        return next;
      });
    },
    []
  );

  const deselectAll = useCallback(() => {
    setSelectedEmails(new Map());
  }, []);

  return {
    selectedEmails,
    selectedCount,
    isSelected,
    toggleSelect,
    selectAll,
    deselectAll,
  };
}
